import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Package, Plus, Trash2 } from "lucide-react";

function MaterialRow({ material, index, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        placeholder="Beschreibung"
        value={material.description}
        onChange={(e) => onUpdate(index, "description", e.target.value)}
        className="flex-1 h-11 md:h-9 text-base md:text-sm"
        data-testid={`material-desc-${index}`}
      />
      <Input
        type="number"
        inputMode="numeric"
        min="1"
        value={material.quantity}
        onChange={(e) => onUpdate(index, "quantity", e.target.value)}
        onBlur={(e) => {
          const n = parseInt(e.target.value, 10);
          onUpdate(index, "quantity", Number.isFinite(n) && n >= 1 ? n : 1);
        }}
        className="w-20 h-11 md:h-9 text-base md:text-sm text-center"
        data-testid={`material-qty-${index}`}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onRemove(index)}
        className="text-destructive hover:text-destructive flex-shrink-0 h-11 w-11 md:h-9 md:w-9"
        data-testid={`remove-material-${index}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function ServiceReportTimeMaterials({
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  workHours,
  travelHours,
  setTravelHours,
  breakMinutes,
  setBreakMinutes,
  totalHours,
  materials,
  onAddMaterial,
  onUpdateMaterial,
  onRemoveMaterial,
}) {
  return (
    <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
            <Clock className="w-5 h-5" />
            Zeiterfassung
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 md:pt-0 grid grid-cols-2 gap-3 md:gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_time">Uhrzeit von</Label>
            <Input
              id="start_time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="h-11 md:h-9 text-base md:text-sm"
              data-testid="start-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_time">Uhrzeit bis</Label>
            <Input
              id="end_time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="h-11 md:h-9 text-base md:text-sm"
              data-testid="end-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="break_minutes">Pause (Min.)</Label>
            <Input
              id="break_minutes"
              type="number"
              inputMode="numeric"
              min="0"
              step="5"
              value={breakMinutes}
              onChange={(e) => setBreakMinutes(e.target.value)}
              placeholder="0"
              className="h-11 md:h-9 text-base md:text-sm"
              data-testid="break-minutes-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="travel_hours">Fahrzeit (h)</Label>
            <Input
              id="travel_hours"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.5"
              value={travelHours}
              onChange={(e) => setTravelHours(e.target.value)}
              placeholder="0.0"
              className="h-11 md:h-9 text-base md:text-sm"
              data-testid="travel-hours-input"
            />
          </div>
          <div className="col-span-2 pt-3 md:pt-4 border-t space-y-1">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Arbeitszeit{parseInt(breakMinutes, 10) > 0 && startTime && endTime ? ` (abzgl. ${parseInt(breakMinutes, 10)} Min. Pause)` : ""}</span>
              <span className="font-medium" data-testid="work-hours-display">{workHours} Stunden</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Gesamtzeit</span>
              <span className="text-lg md:text-xl font-semibold" data-testid="total-hours-display">{totalHours} Stunden</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 md:p-6 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
              <Package className="w-5 h-5" />
              Material
            </CardTitle>
            <CardDescription className="text-sm mt-1">Verwendetes Material</CardDescription>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onAddMaterial} data-testid="add-material-btn" className="shrink-0">
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Hinzufügen</span>
          </Button>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
          {materials.length > 0 ? (
            <div className="space-y-3">
              {materials.map((material, index) => (
                <MaterialRow
                  key={material.id || `material-${index}`}
                  material={material}
                  index={index}
                  onUpdate={onUpdateMaterial}
                  onRemove={onRemoveMaterial}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 md:py-8 text-muted-foreground text-sm">Kein Material hinzugefügt</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
